import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchCart = createAsyncThunk(
  "numberCart/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/cart");
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load cart");
    }
  }
);

export const addToCart = createAsyncThunk(
  "numberCart/add",
  async (item, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/didlogic/cart/add", item);
      dispatch(showToast({ message: `+${item.number} added to cart`, variant: "success" }));
      return res.data.data; // updated items array
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to add to cart";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  "numberCart/remove",
  async (did_id, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.delete(`/didlogic/cart/remove/${did_id}`);
      dispatch(showToast({ message: "Number removed from cart", variant: "success" }));
      return res.data.data; // updated items array
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to remove from cart");
    }
  }
);

export const clearCart = createAsyncThunk(
  "numberCart/clear",
  async (_, { rejectWithValue }) => {
    try {
      await api.delete("/didlogic/cart/clear");
      return [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to clear cart");
    }
  }
);

// Called after a number is successfully purchased — removes it from cart silently
export const removePurchasedFromCart = createAsyncThunk(
  "numberCart/removePurchased",
  async (did_id, { rejectWithValue }) => {
    try {
      await api.post("/didlogic/cart/remove-purchased", { did_id });
      return did_id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const numberCartSlice = createSlice({
  name: "numberCart",
  initialState: {
    items:   [],
    loading: false,
    adding:  null,   // did_id currently being added (for per-row loading state)
    removing: null,  // did_id currently being removed
    error:   null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCart.pending,   (s) => { s.loading = true; })
      .addCase(fetchCart.fulfilled, (s, a) => { s.loading = false; s.items = a.payload; })
      .addCase(fetchCart.rejected,  (s) => { s.loading = false; })

      // Add
      .addCase(addToCart.pending,   (s, a) => { s.adding = a.meta.arg.did_id; })
      .addCase(addToCart.fulfilled, (s, a) => { s.adding = null; s.items = a.payload; })
      .addCase(addToCart.rejected,  (s) => { s.adding = null; })

      // Remove
      .addCase(removeFromCart.pending,   (s, a) => { s.removing = a.meta.arg; })
      .addCase(removeFromCart.fulfilled, (s, a) => { s.removing = null; s.items = a.payload; })
      .addCase(removeFromCart.rejected,  (s) => { s.removing = null; })

      // Clear
      .addCase(clearCart.fulfilled, (s) => { s.items = []; })

      // Remove purchased (silent)
      .addCase(removePurchasedFromCart.fulfilled, (s, a) => {
        s.items = s.items.filter((i) => i.did_id !== a.payload);
      });
  },
});

export default numberCartSlice.reducer;
